<template>
  <div>
    <header-component />
    <div class="px-4 py-4 max-w-3xl mx-auto">

      <div class="flex items-center gap-3 mb-5">
        <router-link to="/branch-transfers" class="text-blue-600 hover:underline text-sm">← Back</router-link>
        <h2 class="text-lg font-bold text-gray-800">Transfer Detail</h2>
      </div>

      <div v-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>

      <div v-else-if="transfer">

        <!-- Status banner -->
        <div :class="statusBannerClass" class="rounded-xl px-5 py-3 mb-4 flex items-center justify-between">
          <span class="font-semibold capitalize text-sm">{{ transfer.status }}</span>
          <span v-if="transfer.status === 'accepted'" class="text-xs opacity-80">
            Accepted by {{ transfer.accepted_by_name }}
          </span>
          <span v-if="transfer.status === 'rejected'" class="text-xs opacity-80">
            {{ transfer.rejection_reason }}
          </span>
        </div>

        <!-- Info card -->
        <div class="bg-white border border-gray-200 rounded-xl p-5 mb-4 grid grid-cols-2 gap-y-3 text-sm">
          <div>
            <span class="text-gray-500 text-xs block">From Branch</span>
            <span class="font-medium text-gray-800">{{ transfer.from_branch_name }}</span>
          </div>
          <div>
            <span class="text-gray-500 text-xs block">To Branch</span>
            <span class="font-medium text-gray-800">{{ transfer.to_branch_name }}</span>
          </div>
          <div>
            <span class="text-gray-500 text-xs block">Created By</span>
            <span class="text-gray-700">{{ transfer.created_by_name }}</span>
          </div>
          <div>
            <span class="text-gray-500 text-xs block">Date</span>
            <span class="text-gray-700">{{ formatDate(transfer.created_at) }}</span>
          </div>
          <div v-if="transfer.notes" class="col-span-2">
            <span class="text-gray-500 text-xs block">Notes</span>
            <span class="text-gray-700">{{ transfer.notes }}</span>
          </div>
        </div>

        <!-- Items -->
        <div class="bg-white border border-gray-200 rounded-xl p-5 mb-4">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Items</h3>
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b text-xs text-gray-500 uppercase">
                <th class="py-2 text-left">Product</th>
                <th class="py-2 text-right">Qty</th>
                <th class="py-2 text-right">Unit Price</th>
                <th class="py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.id" class="border-b">
                <td class="py-2 text-gray-800">{{ item.product_name }}</td>
                <td class="py-2 text-right text-gray-700">{{ item.quantity }}</td>
                <td class="py-2 text-right text-gray-700">₹ {{ Number(item.unit_price).toFixed(2) }}</td>
                <td class="py-2 text-right font-medium text-gray-900">₹ {{ Number(item.total).toFixed(2) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" class="py-3 text-right font-semibold text-gray-700">Total Amount</td>
                <td class="py-3 text-right font-bold text-gray-900">₹ {{ Number(transfer.total_amount).toFixed(2) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Accept / Reject — only for pending transfers destined for current user's branch -->
        <div v-if="transfer.status === 'pending' && canActOnTransfer"
          class="bg-white border border-gray-200 rounded-xl p-5">
          <p class="text-sm text-gray-600 mb-4">
            This transfer is pending acceptance.
          </p>
          <div class="flex gap-3">
            <base-button type="secondary" @click="showRejectModal = true" class="flex-1">Reject</base-button>
            <base-button type="primary" :disabled="acting" @click="accept" class="flex-1">
              <span v-if="acting">Processing...</span>
              <span v-else>Accept Transfer</span>
            </base-button>
          </div>
        </div>

      </div>

      <!-- Reject modal -->
      <div v-if="showRejectModal"
        class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
          <h3 class="text-lg font-bold text-gray-800 mb-4">Reject Transfer</h3>
          <label class="block text-sm font-medium text-gray-700 mb-1">Reason</label>
          <textarea v-model="rejectionReason" rows="3" placeholder="Reason for rejection (required)"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none mb-1"></textarea>
          <p v-if="rejectError" class="text-red-500 text-xs mb-3">{{ rejectError }}</p>
          <div class="flex gap-3 mt-4">
            <base-button type="secondary" @click="showRejectModal = false; rejectionReason = ''; rejectError = ''"
              class="flex-1">Cancel</base-button>
            <base-button type="primary" :disabled="acting" @click="reject" class="flex-1">
              <span v-if="acting">Rejecting...</span>
              <span v-else>Confirm Reject</span>
            </base-button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";

const BASE = endpoints.LARAVEL_BASE_URL;

export default {
  name: "BranchTransferDetail",
  data() {
    return {
      transfer: null,
      items: [],
      loading: false,
      acting: false,
      currentBranchId: null,
      isAdmin: false,
      showRejectModal: false,
      rejectionReason: "",
      rejectError: "",
    };
  },
  computed: {
    canActOnTransfer() {
      if (!this.transfer) return false;
      if (this.isAdmin) return true;
      return this.currentBranchId && this.currentBranchId === this.transfer.to_branch_id;
    },
    statusBannerClass() {
      return {
        pending:  "bg-yellow-50 border border-yellow-200 text-yellow-800",
        accepted: "bg-green-50 border border-green-200 text-green-800",
        rejected: "bg-red-50 border border-red-200 text-red-800",
      }[(this.transfer && this.transfer.status)] || "bg-gray-50";
    },
  },
  created() {
    this.fetchMe();
    this.fetchTransfer();
  },
  methods: {
    async fetchMe() {
      try {
        const res = await new Api().call("get", `${BASE}/api/v1/me`);
        this.currentBranchId = res.data.current_branch_id;
        this.isAdmin = (res.data.role_label || "").toLowerCase() === "admin";
      } catch (e) {
        console.error(e);
      }
    },
    async fetchTransfer() {
      this.loading = true;
      try {
        const res = await new Api().call(
          "get",
          `${BASE}/api/v1/branch-transfers/${this.$route.params.id}`
        );
        this.transfer = res.data.transfer;
        this.items    = res.data.items;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async accept() {
      this.acting = true;
      try {
        await new Api().call(
          "post",
          `${BASE}/api/v1/branch-transfers/${this.$route.params.id}/accept`
        );
        window.notify({ message: "Transfer accepted. Inventory updated and sale recorded.", type: "success", title: "Done" });
        this.fetchTransfer();
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.message) || "Failed to accept transfer";
        window.notify({ message: msg, type: "danger", title: "Error" });
      } finally {
        this.acting = false;
      }
    },
    async reject() {
      if (!this.rejectionReason.trim() || this.rejectionReason.trim().length < 5) {
        this.rejectError = "Please provide a reason (min 5 characters)";
        return;
      }
      this.acting = true;
      try {
        await new Api().call(
          "post",
          `${BASE}/api/v1/branch-transfers/${this.$route.params.id}/reject`,
          { rejection_reason: this.rejectionReason }
        );
        window.notify({ message: "Transfer rejected.", type: "success", title: "Done" });
        this.showRejectModal = false;
        this.fetchTransfer();
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.message) || "Failed to reject transfer";
        window.notify({ message: msg, type: "danger", title: "Error" });
      } finally {
        this.acting = false;
      }
    },
    formatDate(val) {
      if (!val) return "—";
      return new Date(val + "Z").toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>
